import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import ScheduleDialog from "@/components/app/ScheduleDialog";
import ScheduleWeekDialog from "@/components/app/ScheduleWeekDialog";
import { toast } from "sonner";
import {
  Plus, Copy, Pencil, Trash2, CheckCircle2, AlertCircle, Calendar,
  Linkedin, Twitter, Instagram, Facebook, AtSign, Cloud, ChevronLeft, ChevronRight,
  Share2, LayoutList, LayoutGrid as GridIcon, Hash, Clock, CalendarPlus, Download,
} from "lucide-react";
import OtterIcon from "@/components/app/OtterIcon";

const PLATFORM_META = {
  linkedin:  { name: "LinkedIn", icon: Linkedin, accent: "text-[#0a66c2]", bg: "bg-[#0a66c2]/10" },
  x:         { name: "X",        icon: Twitter,  accent: "text-foreground", bg: "bg-foreground/10" },
  instagram: { name: "Instagram", icon: Instagram, accent: "text-[#d6249f]", bg: "bg-[#d6249f]/10" },
  facebook:  { name: "Facebook", icon: Facebook, accent: "text-[#1877f2]", bg: "bg-[#1877f2]/10" },
  threads:   { name: "Threads",  icon: AtSign,   accent: "text-foreground", bg: "bg-foreground/10" },
  bluesky:   { name: "Bluesky",  icon: Cloud,    accent: "text-[#0085ff]", bg: "bg-[#0085ff]/10" },
};

const STATUS_META = {
  pending:  { label: "Pending",  cls: "border-amber-300 text-amber-800 bg-amber-50" },
  posted:   { label: "Posted",   cls: "border-emerald-300 text-emerald-700 bg-emerald-50" },
  skipped:  { label: "Skipped",  cls: "border-slate-300 text-slate-600 bg-slate-50" },
  missed:   { label: "Missed",   cls: "border-red-300 text-red-700 bg-red-50" },
};

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }

function copyFull(p) {
  const text = (p.content + (p.hashtags?.length ? "\n\n" + p.hashtags.map(h => `#${h}`).join(" ") : "")).trim();
  navigator.clipboard.writeText(text)
    .then(() => toast.success("Copied"))
    .catch(() => toast.error("Copy failed"));
}

function shareIntent(p) {
  const full = (p.content + (p.hashtags?.length ? "\n\n" + p.hashtags.map(h => `#${h}`).join(" ") : "")).trim();
  if (p.platform === "x") {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(full)}`, "_blank");
    return;
  }
  if (p.platform === "linkedin") {
    copyFull(p);
    window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank");
    toast.info("Text copied — paste into LinkedIn composer");
    return;
  }
  if (p.platform === "facebook") {
    copyFull(p);
    window.open("https://www.facebook.com/", "_blank");
    toast.info("Text copied — paste into Facebook composer");
    return;
  }
  copyFull(p);
  toast.info(`Text copied — paste into ${PLATFORM_META[p.platform]?.name || "your app"}`);
}

function PostRow({ post, projectName, onEdit, onDelete, onMarkPosted }) {
  const M = PLATFORM_META[post.platform] || {};
  const Icon = M.icon || Calendar;
  const S = STATUS_META[post.status] || STATUS_META.pending;
  const isDue = post.status === "pending" && new Date(post.scheduled_at).getTime() <= Date.now();
  return (
    <div className={`border bg-white p-4 ${isDue ? "border-foreground border-2" : "border-border"}`} data-testid={`queue-row-${post.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 grid place-items-center ${M.bg || ""}`}>
            <Icon className={`w-4 h-4 ${M.accent || ""}`} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold tracking-tight text-base">{M.name || post.platform}</span>
              <span className={`border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-widest ${S.cls}`}>{S.label}</span>
              {isDue && <span className="font-mono-data text-[10px] uppercase tracking-widest text-foreground border border-foreground px-2 py-0.5">due now</span>}
            </div>
            <div className="font-mono-data text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> {fmtDateTime(post.scheduled_at)}
              {projectName && <span className="ml-1.5">· {projectName}</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {post.status === "pending" && (
            <Button size="sm" className="rounded-none h-8 bg-foreground hover:bg-foreground/90" onClick={() => onMarkPosted(post)} data-testid={`queue-mark-posted-${post.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1.5" /> Mark posted
            </Button>
          )}
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => shareIntent(post)} data-testid={`queue-share-${post.id}`}>
            <Share2 className="w-3 h-3 mr-1.5" /> Share
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => copyFull(post)} data-testid={`queue-copy-${post.id}`}>
            <Copy className="w-3 h-3 mr-1.5" /> Copy
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => onEdit(post)} data-testid={`queue-edit-${post.id}`}>
            <Pencil className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8 hover:text-destructive" onClick={() => onDelete(post)} data-testid={`queue-delete-${post.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
      {post.title && <p className="font-semibold tracking-tight text-sm mt-3">{post.title}</p>}
      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed mt-1.5 text-foreground/90">{post.content}</pre>
      {post.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-dashed border-border">
          {post.hashtags.map((h, i) => (
            <Badge key={i} variant="outline" className="rounded-none font-mono-data text-[10px] uppercase">
              <Hash className="w-2.5 h-2.5 mr-0.5" />{h}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthGrid({ posts, currentMonth, onPrev, onNext, onPick }) {
  const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const firstWeekday = start.getDay();
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = useMemo(() => {
    const m = {};
    for (const p of posts) {
      const k = ymd(new Date(p.scheduled_at));
      m[k] = m[k] || [];
      m[k].push(p);
    }
    return m;
  }, [posts]);

  const monthLabel = currentMonth.toLocaleString(undefined, { month: "long", year: "numeric" });
  return (
    <div className="border border-border bg-white" data-testid="calendar-month">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="font-semibold tracking-tight text-base">{monthLabel}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={onPrev} className="rounded-none h-8 w-8 p-0" data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={onNext} className="rounded-none h-8 w-8 p-0" data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="px-2 py-1.5 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          const k = cell ? ymd(cell) : null;
          const day = k ? (byDay[k] || []) : [];
          const isToday = cell && ymd(cell) === ymd(new Date());
          return (
            <button
              key={i}
              onClick={() => cell && onPick(cell)}
              disabled={!cell}
              className={`min-h-[88px] border-t border-l border-border last:border-r-0 [&:nth-child(7n)]:border-r [&:nth-child(-n+7)]:border-t-0 p-2 text-left ${
                !cell ? "bg-secondary/20 cursor-default" :
                isToday ? "bg-amber-50 hover:bg-amber-100" : "bg-white hover:bg-secondary/40"
              }`}
              data-testid={cell ? `calendar-day-${k}` : undefined}
            >
              {cell && (
                <>
                  <div className="font-mono-data text-xs">{cell.getDate()}</div>
                  <div className="mt-1 space-y-0.5">
                    {day.slice(0, 3).map((p) => {
                      const M = PLATFORM_META[p.platform] || {};
                      const Ic = M.icon || Calendar;
                      const dim = p.status !== "pending" ? "opacity-50" : "";
                      return (
                        <div key={p.id} className={`flex items-center gap-1 text-[10px] truncate ${dim}`} title={p.title || p.content.slice(0, 50)}>
                          <Ic className={`w-2.5 h-2.5 shrink-0 ${M.accent || ""}`} />
                          <span className="font-mono-data shrink-0">{fmtTime(p.scheduled_at)}</span>
                          <span className="truncate">{p.title || p.content.slice(0, 30)}</span>
                        </div>
                      );
                    })}
                    {day.length > 3 && <div className="font-mono-data text-[10px] text-muted-foreground">+{day.length - 3} more</div>}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function SocialCalendarPanel({
  scope = "global",       // "global" | "project"
  projectId = null,       // when scope === "project"
  projects = [],
}) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("list"); // list | month
  const [month, setMonth] = useState(() => new Date());
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [weekDlgOpen, setWeekDlgOpen] = useState(false);

  const projectMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);

  const load = async () => {
    try {
      const params = {};
      if (scope === "project" && projectId) params.project_id = projectId;
      const { data } = await api.get("/social/queue", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load queue");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, projectId]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((p) => p.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length, pending: 0, posted: 0, missed: 0, skipped: 0 };
    items.forEach((p) => { if (c[p.status] !== undefined) c[p.status]++; });
    return c;
  }, [items]);

  const onNew = () => { setEditing(null); setDlgOpen(true); };
  const onEdit = (p) => { setEditing(p); setDlgOpen(true); };
  const onDelete = async (p) => {
    if (!confirm("Delete this scheduled post?")) return;
    try {
      await api.delete(`/social/queue/${p.id}`);
      setItems((s) => s.filter((x) => x.id !== p.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const onMarkPosted = async (p) => {
    try {
      const { data } = await api.patch(`/social/queue/${p.id}`, { status: "posted" });
      setItems((s) => s.map((x) => (x.id === p.id ? data : x)));
      toast.success(`Marked ${PLATFORM_META[p.platform]?.name || p.platform} post as posted`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const onSaved = (saved) => {
    setItems((s) => {
      const exists = s.find((x) => x.id === saved.id);
      if (exists) return s.map((x) => (x.id === saved.id ? saved : x));
      return [...s, saved].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
    });
  };
  const pickDay = (date) => {
    const d = new Date(date);
    d.setHours(9, 0, 0, 0);
    setEditing({ scheduled_at: d.toISOString() });
    setDlgOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-none h-9 border-foreground/30 w-[180px]" data-testid="queue-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All · {counts.all}</SelectItem>
              <SelectItem value="pending">Pending · {counts.pending}</SelectItem>
              <SelectItem value="posted">Posted · {counts.posted}</SelectItem>
              <SelectItem value="missed">Missed · {counts.missed}</SelectItem>
              <SelectItem value="skipped">Skipped · {counts.skipped}</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex border border-border">
            <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-widest ${view === "list" ? "bg-foreground text-background" : "bg-white"}`} data-testid="queue-view-list">
              <LayoutList className="inline w-3.5 h-3.5 mr-1" /> List
            </button>
            <button onClick={() => setView("month")} className={`px-3 py-1.5 text-xs font-mono-data uppercase tracking-widest border-l border-border ${view === "month" ? "bg-foreground text-background" : "bg-white"}`} data-testid="queue-view-month">
              <GridIcon className="inline w-3.5 h-3.5 mr-1" /> Month
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {scope === "project" && projectId && (
            <Button onClick={() => setWeekDlgOpen(true)} variant="outline"
              className="rounded-none h-9 border-foreground/30"
              data-testid="queue-auto-schedule-week">
              <CalendarPlus className="w-4 h-4 mr-1.5" /> Auto-schedule a week
            </Button>
          )}
          <Button variant="outline" disabled={items.length === 0}
            onClick={async () => {
              try {
                const res = await api.get("/social/queue/export.csv", { responseType: "blob" });
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = "social-calendar.csv";
                document.body.appendChild(a); a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success("Downloaded social-calendar.csv");
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
            }}
            className="rounded-none h-9" data-testid="queue-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
          <Button onClick={onNew} className="rounded-none h-9 bg-foreground hover:bg-foreground/90" data-testid="queue-new-post">
            <Plus className="w-4 h-4 mr-1.5" /> New post
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-foreground/30 p-12 text-center" data-testid="queue-empty">
          <div className="w-12 h-12 mx-auto bg-foreground grid place-items-center text-background mb-3">
            <OtterIcon size={28} />
          </div>
          <h3 className="font-display tracking-tight text-xl">Queue&apos;s empty — Arlo&apos;s out of posts.</h3>
          <p className="text-sm text-muted-foreground mt-1">Feed him a draft and he&apos;ll fan it out across LinkedIn, X and the rest.</p>
          <Button onClick={onNew} className="mt-5 rounded-none bg-foreground hover:bg-foreground/90" data-testid="queue-empty-new">
            <Plus className="w-4 h-4 mr-1.5" /> Schedule first post
          </Button>
        </div>
      ) : view === "list" ? (
        <div className="space-y-3" data-testid="queue-list">
          {filtered.map((p) => (
            <PostRow key={p.id} post={p}
              projectName={p.project_id ? projectMap[p.project_id] : null}
              onEdit={onEdit} onDelete={onDelete} onMarkPosted={onMarkPosted} />
          ))}
        </div>
      ) : (
        <MonthGrid posts={filtered} currentMonth={month}
          onPrev={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
          onNext={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
          onPick={pickDay} />
      )}

      <ScheduleDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        initial={editing || {}}
        projects={projects}
        defaultProjectId={scope === "project" ? projectId : null}
        onSaved={onSaved}
      />

      {scope === "project" && projectId && (
        <ScheduleWeekDialog
          open={weekDlgOpen}
          onOpenChange={setWeekDlgOpen}
          projectId={projectId}
          onCompleted={() => load()}
        />
      )}

      {counts.missed > 0 && statusFilter !== "missed" && (
        <div className="border border-amber-300 bg-amber-50 p-3 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700" />
          <span>{counts.missed} post(s) marked as missed.</span>
          <button className="underline ml-1" onClick={() => setStatusFilter("missed")} data-testid="queue-show-missed">View</button>
        </div>
      )}
    </div>
  );
}
